import { ChangeEvent, DragEvent, useState, useRef, useEffect } from "react";

interface MediaDropzoneProps {
  // The field in context to update
  contextField: string;
  // Function to update the value in context
  onFileChange: (files: File[]) => void;
  // Existing files to display (always an array, empty if none)
  existingFiles?: File[];
  // Accepted file types
  acceptedTypes?: string[];
  // Label text
  label?: string;
}

const MediaDropzone: React.FC<MediaDropzoneProps> = ({
  contextField,
  onFileChange,
  existingFiles = [],
  acceptedTypes = ["image/jpeg", "image/png", "video/mp4"],
  label = "Drop your media here or browse",
}) => {
  // Keep track of files internally
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);

  // Use ref to prevent infinite loops
  const prevExistingFilesRef = useRef<string>("");

  // Initialize once and update when existingFiles changes
  useEffect(() => {
    // Create a simple signature based on files' metadata
    const currentSignature = existingFiles
      ?.map((file) => `${file.name}-${file.size}`)
      .join("|");

    // Only update if files have actually changed
    if (currentSignature !== prevExistingFilesRef.current) {
      setMediaFiles([...existingFiles]);
      prevExistingFilesRef.current = currentSignature;
    }
  }, [existingFiles]);

  const handleFileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleFileUpload = (files: File[]) => {
    const supportedFiles = files.filter((file) =>
      acceptedTypes.some((type) => {
        // Handle wildcards like "image/*"
        if (type.endsWith("/*")) {
          const mainType = type.split("/")[0];
          return file.type.startsWith(`${mainType}/`);
        }
        return type === file.type;
      })
    );

    const updatedFiles = [...mediaFiles, ...supportedFiles];
    setMediaFiles(updatedFiles);

    // Call the callback to update parent component/context
    onFileChange(updatedFiles);
  };

  const handleBrowse = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFileUpload(files);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = mediaFiles.filter((_, i) => i !== index);
    setMediaFiles(updatedFiles);

    // Call the callback to update parent component/context
    onFileChange(updatedFiles);
  };

  // Create a readable accepted file formats string
  const getAcceptedFormatsText = () => {
    const formats = acceptedTypes.map((type) => {
      if (type.includes("image")) return "images";
      if (type.includes("video")) return "videos";
      if (type.includes("audio")) return "audio";
      // Make sure we have a fallback for any unusual formats
      const parts = type.split("/");
      return parts.length > 1 ? parts[1] : type;
    });

    // Get unique formats without using Set
    const uniqueFormats: string[] = [];
    formats.forEach((format) => {
      if (!uniqueFormats.includes(format)) {
        uniqueFormats.push(format);
      }
    });

    return `(Supports ${uniqueFormats.join(", ")})`;
  };

  // Generate accept attribute string for the file input
  const getAcceptString = () => acceptedTypes.join(",");

  // Create a unique ID for the file input to prevent conflicts when multiple instances exist
  const inputId = `fileInput-${contextField}`;

  return (
    <div className="flex flex-col items-center justify-center w-full">
      <div
        className={`border-2 border-dashed border-gray-400 p-10 rounded-lg w-full text-center transition-colors ${
          dragging ? "bg-gray-100" : ""
        }`}
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <p className="text-gray-600">
          {dragging ? "Drop your files here" : label}
        </p>
        <p className="text-[12px] text-gray-400 mb-4">
          {getAcceptedFormatsText()}
        </p>

        {/* Browse Button */}
        <input
          type="file"
          accept={getAcceptString()}
          multiple
          className="hidden"
          id={inputId}
          onChange={handleBrowse}
        />
        <label
          htmlFor={inputId}
          className="mt-2 px-4 py-2 bg-black text-white rounded cursor-pointer"
        >
          Browse
        </label>
      </div>

      {/* Preview Media */}
      <div className="mt-4 w-full max-w-lg">
        {mediaFiles.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            {mediaFiles.map((file, index) => (
              <div key={index} className="relative flex flex-col items-center">
                {/* "X" Button */}
                <button
                  type="button"
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold"
                  onClick={() => handleRemoveFile(index)}
                >
                  ×
                </button>

                {file.type.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt="uploaded preview"
                    className="w-full h-32 object-cover rounded"
                  />
                ) : file.type.startsWith("video/") ? (
                  <video
                    controls
                    src={URL.createObjectURL(file)}
                    className="w-full h-32 rounded"
                  ></video>
                ) : file.type.startsWith("audio/") ? (
                  <audio
                    controls
                    src={URL.createObjectURL(file)}
                    className="w-full mt-2"
                  ></audio>
                ) : (
                  <div className="w-full h-32 flex items-center justify-center bg-gray-200 rounded">
                    <span>File: {file.name}</span>
                  </div>
                )}
                <p className="text-sm mt-2 truncate w-full text-center">
                  {file.name}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaDropzone;
